import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../lib/api.js";
import { addDays, datesCovered, today, weekStart } from "../../lib/dates.js";
import { layOut } from "../../lib/lanes.js";
import { bandsForRow, spanAria, spanOn } from "../../lib/span.js";
import { useSession } from "../../lib/session.js";
import { foregroundOn, initialOf, tintOf } from "../../tokens/palette.js";
import type { Occurrence } from "../../lib/types.js";
import { DesktopChrome } from "./DesktopChrome.js";

/** 58px an hour, from the handoff. */
const HOUR = 58;
const DEFAULT_FROM = 7;
const DEFAULT_TO = 20;

/**
 * One day's worth of a timed event, in minutes from the top of the grid.
 *
 * An event that runs past midnight is not one block on one day; it is a piece
 * of Friday and a piece of Saturday, and drawing it whole in Friday's column
 * would put a five-hour bar through the bottom of the grid and leave Saturday
 * morning looking free. So each day gets its own clipped piece, and the pieces
 * say which of their edges was cut.
 */
interface Segment {
  occurrence: Occurrence;
  /** Minutes from midnight local, clipped to this day. */
  startMin: number;
  endMin: number;
  fromEarlier: boolean;
  intoLater: boolean;
}

function segmentOn(occurrence: Occurrence, date: string, zone: string): Segment {
  const dayStart = DateTime.fromISO(date, { zone }).startOf("day");
  const dayEnd = dayStart.plus({ days: 1 });

  const start = DateTime.fromISO(occurrence.starts_at, { zone });
  const end =
    occurrence.ends_at === null ? start : DateTime.fromISO(occurrence.ends_at, { zone });

  const fromEarlier = start < dayStart;
  const intoLater = end > dayEnd;

  const clippedStart = fromEarlier ? dayStart : start;
  const clippedEnd = intoLater ? dayEnd : end;

  /*
   * Minutes are measured against this day's own midnight rather than by
   * subtracting hours, so a spring-forward day is 23 hours long here too and
   * an afternoon event does not slide an hour up the column.
   */
  return {
    occurrence,
    startMin: clippedStart.diff(dayStart, "minutes").minutes,
    endMin: clippedEnd.diff(dayStart, "minutes").minutes,
    fromEarlier,
    intoLater,
  };
}

export function DesktopWeek({
  onView,
}: {
  onView: (view: "week" | "month") => void;
}): React.JSX.Element {
  const { zone } = useSession();
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(() => today(zone));
  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);
  const [now, setNow] = useState(() => DateTime.now().setZone(zone));

  const start = weekStart(anchor, zone);
  const end = addDays(start, 7, zone);
  const todayDate = today(zone);

  const load = useCallback(async () => {
    setOccurrences((await api.events(start, end)).occurrences);
  }, [start, end]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const tick = setInterval(() => setNow(DateTime.now().setZone(zone)), 60_000);
    return () => clearInterval(tick);
  }, [zone]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(start, i, zone)),
    [start, zone],
  );

  const timed = (occurrences ?? []).filter((o) => !o.all_day);
  const allDay = (occurrences ?? []).filter((o) => o.all_day);

  /** Column widths, shared by the body grid and the all-day band overlay. */
  const dayColumns = days
    .map((date) => {
      const d = DateTime.fromISO(date, { zone });
      const weekend = d.weekday === 6 || d.weekday === 7;
      // A weekend column carries less and can afford less room.
      return weekend && date !== todayDate ? "0.7fr" : "1fr";
    })
    .join(" ");

  /*
   * The all-day strip is one grid rather than seven cells, because a run has
   * to be one chip lying across the days it covers. Seven cells can only ever
   * hold seven chips, and the reader has to work out from the repeated title
   * that it is the same trip.
   */
  const spanningAllDay = allDay.filter(
    (o) => datesCovered(o.starts_at, o.ends_at, zone).length > 1,
  );
  const { bands: allDayBands, lanes: allDayLanes } = bandsForRow(spanningAllDay, days, zone);

  const singleAllDay = new Map<string, Occurrence[]>(
    days.map((date) => [
      date,
      allDay.filter(
        (o) =>
          datesCovered(o.starts_at, o.ends_at, zone).length === 1 &&
          datesCovered(o.starts_at, o.ends_at, zone)[0] === date,
      ),
    ]),
  );

  const allDayRows = Math.max(
    1,
    allDayLanes + Math.max(0, ...[...singleAllDay.values()].map((list) => list.length)),
  );

  /** Every day a timed event touches, as its own clipped piece. */
  const segmentsByDay = useMemo(() => {
    const map = new Map<string, Segment[]>();
    for (const date of days) map.set(date, []);
    for (const o of timed) {
      for (const date of datesCovered(o.starts_at, o.ends_at, zone)) {
        map.get(date)?.push(segmentOn(o, date, zone));
      }
    }
    return map;
    // `timed` is derived fresh each render, so the occurrences it came from
    // are the honest dependency.
  }, [occurrences, days, zone]);

  /*
   * The grid is 7am–8pm, which covers a household's day. Rather than clamp an
   * early flight to 7am and show the wrong time, the range stretches to fit
   * whatever is actually there.
   */
  const [fromHour, toHour] = useMemo(() => {
    let lo = DEFAULT_FROM;
    let hi = DEFAULT_TO;

    /*
     * A piece cut at midnight is anchored by the end it reaches, not by the
     * midnight it was cut at. Stretching the grid to a continuation's 12am
     * would open every column to twenty-four hours for the sake of one
     * overnight flight, and the household's day would be a strip of it.
     *
     * The same in reverse for a piece that carries on into tomorrow: an hour
     * past where it starts is enough to see that it does.
     */
    for (const list of segmentsByDay.values()) {
      for (const segment of list) {
        lo = Math.min(
          lo,
          segment.fromEarlier
            ? Math.floor(segment.endMin / 60)
            : Math.floor(segment.startMin / 60),
        );
        hi = Math.max(
          hi,
          segment.intoLater
            ? Math.ceil(segment.startMin / 60) + 1
            : Math.ceil(segment.endMin / 60),
        );
      }
    }

    return [Math.max(0, lo), Math.min(24, hi)];
  }, [segmentsByDay]);

  const hours = Array.from({ length: toHour - fromHour }, (_, i) => fromHour + i);
  const bodyHeight = hours.length * HOUR;
  const bodyMinutes = hours.length * 60;

  const quiet = days.filter(
    (date) =>
      (segmentsByDay.get(date)?.length ?? 0) === 0 &&
      !allDay.some((o) => datesCovered(o.starts_at, o.ends_at, zone).includes(date)),
  );

  return (
    <div className="dk">
      <DesktopChrome
        title={`Week of ${DateTime.fromISO(start, { zone }).toFormat("LLLL d")}`}
        view="week"
        onView={onView}
        onPrev={() => setAnchor(addDays(start, -7, zone))}
        onNext={() => setAnchor(addDays(start, 7, zone))}
        onToday={() => setAnchor(todayDate)}
      />

      <div
        className="dk-grid"
        style={{ gridTemplateColumns: `82px ${dayColumns}` }}
      >
        <div className="dk-gutter dk-gutter--head" />
        {days.map((date) => {
          const d = DateTime.fromISO(date, { zone });
          const weekend = d.weekday === 6 || d.weekday === 7;
          return (
            <button
              key={date}
              type="button"
              className={`dk-colhead${date === todayDate ? " is-today" : ""}${weekend ? " is-weekend" : ""}`}
              onClick={() => void navigate(`/day/${date}`)}
            >
              <span className="dk-colhead__dow">{d.toFormat("ccc")}</span>
              <span className="dk-colhead__num">{d.toFormat("d")}</span>
            </button>
          );
        })}

        <div className="dk-gutter dk-gutter--allday">all day</div>
        <div
          className="dk-alldayrow"
          style={{
            gridColumn: "2 / -1",
            gridTemplateColumns: dayColumns,
            /*
             * A trailing flexible row soaks up whatever height the strip has
             * beyond its chips, so the day grounds behind them — today's
             * paper, the weekend wash — reach the bottom of the strip rather
             * than stopping short of it.
             */
            gridTemplateRows: `repeat(${String(allDayRows)}, 27px) 1fr`,
          }}
        >
          {/* The seven day grounds, behind everything. Today and the weekends
              keep their shading; the chips and bands sit on top. */}
          {days.map((date, column) => {
            const d = DateTime.fromISO(date, { zone });
            const weekend = d.weekday === 6 || d.weekday === 7;
            return (
              <div
                key={date}
                className={`dk-allday${date === todayDate ? " is-today" : ""}${weekend ? " is-weekend" : ""}`}
                style={{ gridColumn: column + 1, gridRow: "1 / -1" }}
              />
            );
          })}

          {allDayBands.map((band) => {
            const lead = band.item.people[0];
            return (
              <button
                key={band.item.occurrence_id}
                type="button"
                className={[
                  "dk-chip",
                  "dk-chip--band",
                  band.clippedStart ? "is-clipped-start" : "",
                  band.clippedEnd ? "is-clipped-end" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={spanAria(band.item, days[band.from] ?? "", zone)}
                style={{
                  gridColumn: `${String(band.from + 1)} / ${String(band.to + 2)}`,
                  gridRow: band.lane + 1,
                  background: lead === undefined ? "var(--paper)" : tintOf(lead.color),
                  borderLeftColor: lead?.color ?? "var(--mid)",
                }}
                onClick={() =>
                  void navigate(
                    `/event/${band.item.event_id}?occ=${encodeURIComponent(band.item.occurrence_id)}`,
                  )
                }
              >
                {band.clippedStart ? `… ${band.item.title}` : band.item.title}
              </button>
            );
          })}

          {days.map((date, column) =>
            (singleAllDay.get(date) ?? []).map((o, index) => {
              const lead = o.people[0];
              return (
                <button
                  key={o.occurrence_id}
                  type="button"
                  className="dk-chip"
                  style={{
                    gridColumn: column + 1,
                    gridRow: allDayLanes + index + 1,
                    background: lead === undefined ? "var(--paper)" : tintOf(lead.color),
                    borderLeftColor: lead?.color ?? "var(--mid)",
                  }}
                  onClick={() =>
                    void navigate(
                      `/event/${o.event_id}?occ=${encodeURIComponent(o.occurrence_id)}`,
                    )
                  }
                >
                  {o.title}
                </button>
              );
            }),
          )}
        </div>

        <div className="dk-gutter" style={{ height: bodyHeight }}>
          {hours.map((hour, index) => (
            <span
              key={hour}
              className={`dk-hour${hour === 12 ? " is-noon" : ""}`}
              style={{ top: index * HOUR }}
            >
              {hour === 12
                ? "noon"
                : DateTime.fromObject({ hour }).toFormat("ha").toLowerCase()}
            </span>
          ))}
        </div>

        {days.map((date) => {
          const d = DateTime.fromISO(date, { zone });
          const weekend = d.weekday === 6 || d.weekday === 7;
          const isToday = date === todayDate;

          const here = segmentsByDay.get(date) ?? [];
          const background = here.filter((s) => s.occurrence.is_background);
          const foreground = here.filter((s) => !s.occurrence.is_background);

          /*
           * Clipped to the visible range as well as to the day. A piece whose
           * own edge is off the top or bottom of the grid is marked as cut,
           * the same as one cut at midnight — from the reader's side of the
           * screen they are the same fact: it carries on past what is shown.
           */
          const top = (segment: Segment): number =>
            Math.max(0, segment.startMin - fromHour * 60);
          const bottom = (segment: Segment): number =>
            Math.min(bodyMinutes, segment.endMin - fromHour * 60);

          const cutAbove = (segment: Segment): boolean =>
            segment.fromEarlier || segment.startMin < fromHour * 60;
          const cutBelow = (segment: Segment): boolean =>
            segment.intoLater || segment.endMin > toHour * 60;

          const placed = layOut(foreground, (segment) => ({
            start: top(segment),
            end: bottom(segment),
          }));

          return (
            <div
              key={date}
              className={`dk-col${isToday ? " is-today" : ""}${weekend ? " is-weekend" : ""}`}
              style={{ height: bodyHeight }}
            >
              {/* School sits behind everything, full width, hatched. */}
              {background.map((segment) => {
                const o = segment.occurrence;
                const height =
                  o.ends_at === null ? 28 : ((bottom(segment) - top(segment)) / 60) * HOUR;
                return (
                  <button
                    key={`${o.occurrence_id}:${date}`}
                    type="button"
                    className={[
                      "dk-bg",
                      cutAbove(segment) ? "is-from-earlier" : "",
                      cutBelow(segment) ? "is-into-later" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{ top: (top(segment) / 60) * HOUR, height: Math.max(28, height) }}
                    onClick={() =>
                      void navigate(
                        `/event/${o.event_id}?occ=${encodeURIComponent(o.occurrence_id)}`,
                      )
                    }
                  >
                    <span>{o.title}</span>
                  </button>
                );
              })}

              {placed.map(({ item: segment, lane, lanes }) => {
                const o = segment.occurrence;
                const startMin = top(segment);
                const endMin = bottom(segment);
                const lead = o.people[0];
                const span = spanOn(o, date, zone);

                return (
                  <button
                    key={`${o.occurrence_id}:${date}`}
                    type="button"
                    className={[
                      "dk-event",
                      // A piece cut at midnight — or at the edge of the
                      // visible hours — is marked on that side, so it reads
                      // as a slice of something longer rather than a short
                      // event that happens to start on the hour.
                      cutAbove(segment) ? "is-from-earlier" : "",
                      cutBelow(segment) ? "is-into-later" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={spanAria(o, date, zone)}
                    style={{
                      top: (startMin / 60) * HOUR,
                      height: Math.max(28, ((endMin - startMin) / 60) * HOUR),
                      left: `${String((lane / lanes) * 100)}%`,
                      width: `${String((1 / lanes) * 100)}%`,
                      background: lead === undefined ? "var(--paper-raised)" : tintOf(lead.color),
                      borderLeftColor: lead?.color ?? "var(--mid)",
                    }}
                    onClick={() =>
                      void navigate(
                        `/event/${o.event_id}?occ=${encodeURIComponent(o.occurrence_id)}`,
                      )
                    }
                  >
                    <span className="dk-event__title">{o.title}</span>
                    <span className="dk-event__time">
                      {cutAbove(segment)
                        ? // It did not start today. Naming the hour it did
                          // would print yesterday's time on this morning.
                          `→ ${DateTime.fromISO(o.ends_at ?? o.starts_at, { zone })
                            .toFormat("h:mm")
                            .toLowerCase()}`
                        : `${DateTime.fromISO(o.starts_at, { zone })
                            .toFormat("h:mm")
                            .toLowerCase()}${span.intoLater ? " →" : ""}`}
                    </span>
                    <span className="dk-event__who">
                      {o.people.map((p) => (
                        <span
                          key={p.id}
                          className="disc"
                          style={{
                            width: 15,
                            height: 15,
                            fontSize: 8.5,
                            background: p.color,
                            color: foregroundOn(p.color),
                          }}
                        >
                          {initialOf(p.display_name)}
                        </span>
                      ))}
                    </span>
                  </button>
                );
              })}

              {/* The only live element on the screen. */}
              {isToday && now.hour >= fromHour && now.hour < toHour && (
                <div
                  className="dk-now"
                  style={{ top: (((now.hour - fromHour) * 60 + now.minute) / 60) * HOUR }}
                >
                  <span className="dk-now__dot" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <footer className="dk-foot">
        <span>
          {quiet.length === 0
            ? "Every day has something."
            : `Quiet: ${quiet
                .map((d) => DateTime.fromISO(d, { zone }).toFormat("ccc"))
                .join(", ")}`}
        </span>
        <span className="dk-foot__hint">
          Click a column head for that day · click an event to edit
        </span>
      </footer>
    </div>
  );
}
